import { Navigate, Outlet } from 'react-router-dom';

import { ShieldAlert } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import Heading from '@/components/ui/heading';
import { PageSkeleton } from '@/components/ui/page-skeleton';
import Text from '@/components/ui/text';
import { useStaffIdentity } from '@/hooks/admin';
import {
  ControlApiError,
  ControlOriginUnknownError,
  redirectToControlLogin,
} from '@/services/control/control-api-client';
import { isStaff } from '@/types/control/staff';

function AdminMessage({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex w-full max-w-lg flex-col items-start gap-5 px-5 pt-10 sm:gap-4 sm:px-4">
      <Heading level={2}>{title}</Heading>
      <Text variant="muted">{children}</Text>
      {action}
    </div>
  );
}

/**
 * Gate for the cross-tenant admin area.
 *
 * Two separate sessions are in play. Mesh's own `ProtectedRoute` has already
 * established that the user is signed in to the Mesh UI; this layout
 * establishes that the same browser also holds a Control session, and that the
 * Control principal behind it is Hydden staff.
 *
 * The gate is presentation only. control-api enforces staff access on every
 * request, so a user who defeats this sees an admin shell full of 401s and 403s
 * rather than any data.
 */
export default function AdminLayout() {
  // Unconditional: someone on /admin asked for this page, so the tier lookup is
  // wanted even if ADMIN_AREA_ENABLED keeps it out of navigation.
  const {
    data: identity,
    isLoading,
    error,
  } = useStaffIdentity({ enabled: true });

  if (isLoading) {
    return <PageSkeleton />;
  }

  // No Control session. Control owns the login form, so this leaves the SPA
  // entirely. It is offered as a button rather than an automatic redirect: if
  // Control were to bounce back without setting a session, an automatic one
  // would loop, and a loop is much harder to diagnose than a stalled button.
  if (error instanceof ControlApiError && error.isUnauthenticated) {
    return (
      <AdminMessage
        title="Sign in to Control"
        action={
          <Button onClick={() => redirectToControlLogin()}>
            Continue to Control
          </Button>
        }
      >
        {error.message} The admin area reads from control-api, which needs its
        own session alongside your Mesh sign-in.
      </AdminMessage>
    );
  }

  // Local development, or a host that does not follow the `*-core` convention.
  if (error instanceof ControlOriginUnknownError) {
    return (
      <AdminMessage title="control-api is not reachable from this host">
        {error.message}
      </AdminMessage>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-lg px-5 pt-10 sm:px-4">
        <Alert variant="error">
          <ShieldAlert />
          <AlertTitle>Could not load your admin access</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!isStaff(identity ?? null)) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}
